import axiosInstance from "utilities/axios";
import useRoleCheck from "utilities/hooks/roleCheck";
import formatDate from "utilities/formats/formatDate";

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button, Table, Tooltip, Select, Row, Col, Badge, Alert } from "antd";
import {
  PlusOutlined,
  ContainerOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import AddEquipment from "./components/AddEquipment";
import ViewEquipmentModal from "./components/ViewEquipmentModal";
import RequestEquipment from "./components/RequestEquipment";
import Summary from "./components/Summary";
import ClinicRequests from "./components/ClinicRequests";
import ScheduleCalibration from "./components/ScheduleCalibration";

function ClinicEquipment() {
  const clinic = useSelector((state) => state.selectedClinic.clinicData);
  const [equipmentData, setEquipmentData] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [requestData, setRequestData] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showScheduleCalibrationModal, setShowScheduleCalibrationModal] =
    useState(false);
  const [scheduledCalibrations, setScheduledCalibrations] = useState([]);
  const [futureVisit, setFutureVisit] = useState(false);
  const [showRequestEquipmentModal, setShowRequestEquipmentModal] =
    useState(false);

  const [selectedRecordId, setSelectedRecordId] = useState(null); // This will hold the selected equipment's data for details modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { clinic_no } = useParams();
  const { hasPermission } = useRoleCheck();

  const fetchEquipmentData = useCallback(() => {
    const params = { clinics: clinic_no };
    axiosInstance
      .get(`/equipment/inventory`, { params })
      .then((result) => {
        setEquipmentData(result.data);
      })
      .catch((err) => {
        console.error("Error fetching equipment data:", err);
      });
  }, [clinic_no]);

  const fetchRequestData = useCallback(() => {
    axiosInstance
      .get(`/equipment/requests?clinicNo=${clinic_no}`)
      .then((result) => {
        setRequestData(result.data);
      })
      .catch((error) => {
        console.error("Error fetching equipment request data:", error);
      });
  }, [clinic_no]);

  const fetchScheduledCalibrations = useCallback(() => {
    axiosInstance
      .get(`equipment/calibrations/schedule?clinicNo=${clinic_no}`)
      .then((response) => {
        setScheduledCalibrations(response.data);
        var futureVisitDate = response.data.find((calibration) =>
          dayjs(calibration.scheduledDate).isAfter(dayjs())
        );
        if (futureVisitDate) {
          setFutureVisit(futureVisitDate);
        } else {
          setFutureVisit(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching previous calibrations:", error);
      });
  }, [clinic_no]);

  const fetchEquipmentItem = useCallback(() => {
    if (!selectedRecordId) {
      setSelectedRecord(null);
      return;
    }
    axiosInstance
      .get(`/equipment/inventory/${selectedRecordId}`)
      .then((result) => {
        setSelectedRecord(result.data);
      })

      .catch((err) => {
        console.error("Error fetching equipment data:", err);
      });
  }, [selectedRecordId]);

  const filteredData = useMemo(() => {
    if (!equipmentData) {
      return [];
    }
    return equipmentData.filter((item) =>
      selectedCategories.length === 0
        ? true
        : selectedCategories.includes(item.equipment.category)
    );
  }, [equipmentData, selectedCategories]);

  const addableRequests = useMemo(() => {
    return requestData.some(
      (request) =>
        (request.requestType === "New Equipment" ||
          request.requestType === "Transfer") &&
        (request.currentStatus === "Shipped" ||
          request.currentStatus === "Approved")
    );
  }, [requestData]);

  // is true if the clinic status is Pending Migration or Migration Date is in the past 90 days
  const migratedClinic = useMemo(() => {
    return !clinic
      ? false
      : clinic.clinicStatus === "Pending Integration" ||
          (clinic.migrationDate &&
            dayjs(clinic.migrationDate).isAfter(dayjs().subtract(90, "days")));
  }, [clinic]);

  useEffect(() => {
    fetchEquipmentData();
    fetchRequestData();
    fetchEquipmentItem();
    fetchScheduledCalibrations();
  }, [
    fetchEquipmentData,
    fetchRequestData,
    fetchEquipmentItem,
    fetchScheduledCalibrations,
  ]);

  const columns = [
    { title: "Brand", dataIndex: ["equipment", "brand"], key: "brand" },
    { title: "Model", dataIndex: ["equipment", "model"], key: "model" },
    {
      title: "Category",
      dataIndex: ["equipment", "category"],
      key: "category",
    },
    {
      title: "Last Modified",
      dataIndex: ["modified", "date"],
      key: "modified",
      render: (text) => formatDate(text, "short"),
      responsive: ["lg"],
    },
    {
      title: "Condition",
      dataIndex: "condition",
      key: "condition",
      filters: [
        {
          text: "Working",
          value: "Working",
        },
        {
          text: "Not Working",
          value: "Not Working",
        },
      ],
      onFilter: (value, record) => record.condition.indexOf(value) === 0,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        {
          text: "In Use",
          value: "In Use",
        },
        {
          text: "Not In Use",
          value: "Not In Use",
        },
      ],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
    },
    {
      title: "Details",
      dataIndex: "details",
      key: "details",
      render: (_, record) => (
        <Badge
          status="warning"
          dot={
            record.status === "Awaiting Shipping" &&
            hasPermission(
              ["Audiology"],
              ["Sales.Staff", "Sales.RM"],
              clinic?.clinicNo,
              false
            )
          }
        >
          <Button type="primary" onClick={() => setSelectedRecordId(record.id)}>
            Details
          </Button>
        </Badge>
      ),
    },
  ];

  const uniqueCategories = Array.from(
    new Set(equipmentData.map((item) => item.equipment.category))
  );
  return (
    <div>
      <Row gutter={[24, 8]}>
        <Col span={18}>
          <Row wrap justify={"space-between"} gutter={[16, 16]}>
            <Col>
              <Row gutter={4}>
                {hasPermission(
                  ["Audiology.Write", "Audiology.Admin"],
                  ["Sales.RM", "Sales.Staff", "Sales.Exec"],
                  clinic_no,
                  false
                ) && (
                  <Col>
                    <Tooltip title="Request Equipment">
                      <Button
                        icon={<ContainerOutlined />}
                        onClick={() => setShowRequestEquipmentModal(true)}
                      >
                        Request
                      </Button>
                    </Tooltip>
                  </Col>
                )}
                {(addableRequests || migratedClinic || hasPermission(['Audiology.Admin', 'Audiology.Write'])) &&
                  hasPermission(
                    ["Audiology.Admin", "Audiology.Write"],
                    ["Sales.RM", "Sales.Staff", "Sales.Exec"],
                    clinic_no,
                    false
                  ) && (
                    <Col>
                      <Tooltip title="Add items from equipment requests">
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setShowAddModal(true);
                          }}
                        >
                          Add
                        </Button>
                      </Tooltip>
                    </Col>
                  )}
                {hasPermission(
                  ["Audiology.Admin", "Audiology.Write"],
                  [],
                  null,
                  false
                ) && (
                  <Col>
                    <Tooltip title="Schedule Calibration">
                      <Button
                        icon={<ClockCircleOutlined />}
                        onClick={() => setShowScheduleCalibrationModal(true)}
                      >
                        Schedule Calibration
                      </Button>
                    </Tooltip>
                  </Col>
                )}
              </Row>
            </Col>
            <Col>
              <Row gutter={4}>
                {futureVisit &&
                  hasPermission(
                    ["Audiology"],
                    ["Sales.RM", "Sales.Staff", "Sales.Exec"],
                    clinic_no,
                    true
                  ) && (
                    // max height should be height of other items in flex row and should include margin on the right
                    <Col flex="auto">
                      <Alert
                        style={{ maxHeight: "32px" }}
                        message={`Calibration visit scheduled for ${formatDate(
                          futureVisit.scheduledDate,
                          "short"
                        )}`}
                        type="info"
                        showIcon
                      />
                    </Col>
                  )}
                <Col flex="auto">
                  <Select
                    placeholder="Select Category"
                    onChange={(value) => setSelectedCategories(value)}
                    mode="multiple"
                    className="w-48"
                    allowClear
                  >
                    {uniqueCategories.map((cat) => (
                      <Select.Option key={cat} value={cat}>
                        {cat}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Col>
          </Row>

          <Table
            bordered
            style={{ marginTop: "1rem" }}
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            filterData
            sticky={{ offsetHeader: -20 }}
            size={"small"}
          />
        </Col>
        <Col span={6}>
          {/* Equipment Summary */}
          <Summary equipmentData={equipmentData} />
        </Col>
      </Row>
      {/* Equipment Requests */}
      {hasPermission(
        ["Sales.Exec", "Audiology"],
        ["Sales.RM", "Sales.Staff", "Sales.Exec"],
        clinic_no
      ) && <ClinicRequests requestData={requestData} />}

      <ViewEquipmentModal
        open={!!selectedRecordId}
        equipment={selectedRecord}
        onClose={() => setSelectedRecordId(null)}
        updateEquipment={() => {
          fetchEquipmentData();
          fetchEquipmentItem();
        }}
        clinicNo={clinic_no}
      />
      {/* Request Equipment Modal */}
      <RequestEquipment
        isVisible={showRequestEquipmentModal}
        onClose={() => setShowRequestEquipmentModal(false)}
        onAdd={() => fetchRequestData()}
        clinicNo={clinic_no}
      />
      <AddEquipment
        isVisible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={() => {
          fetchEquipmentData();
          fetchRequestData();
        }}
        clinicNo={clinic_no}
        migratedClinic={migratedClinic}
        requests={requestData}
      />
      <ScheduleCalibration
        isVisible={showScheduleCalibrationModal}
        onClose={() => {
          setShowScheduleCalibrationModal(false);
          fetchScheduledCalibrations();
        }}
        clinicNo={clinic_no}
        scheduledCalibrations={scheduledCalibrations}
      />
    </div>
  );
}

export default ClinicEquipment;
